import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import i18n from '../../i18n/index';
import { SEO_PAGES } from './data';

// Reusable AI-generated headshot landing page for SEO / audience routes.
// Usage in routes: <SeoHeadshotPage slug="linkedin" />
const SeoHeadshotPage = ({ slug }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const data = SEO_PAGES[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    setCurrentLanguage(i18n.language || 'en');
    if (data && data.h1) {
      document.title = `${data.h1} | HeadshotsNow.ai`;
    }
  }, [slug, data]);

  if (!data) return null;

  const signupHref = currentLanguage === 'en' ? '/signup' : `/${currentLanguage}/signup`;

  return (
    <main className="bg-black text-white">
      {/* Hero */}
      <section className="section-top-80 px-[15px]">
        <div className="mx-auto max-w-[900px] text-center pt-16 pb-10">
          <span className="inline-block text-xs font-semibold tracking-wide uppercase text-cyan-400 mb-4">
            AI-generated professional headshots
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mb-5">{data.h1}</h1>
          <p className="text-lg text-white/80 max-w-[700px] mx-auto mb-8">{data.intro}</p>
          <Link
            to={signupHref}
            className="inline-block bg-cyan-600 hover:bg-cyan-700 transition-colors text-white font-semibold rounded-full px-8 py-3"
          >
            Get your AI headshots
          </Link>
          <p className="text-xs text-white/50 mt-4">
            Results are AI-generated and may vary with the quality of your uploaded photos.
          </p>
        </div>
      </section>

      {/* How it works */}
      {data.howItWorks && (
        <section className="px-[15px] py-10">
          <div className="mx-auto max-w-[1000px]">
            <h2 className="text-2xl font-bold text-center mb-8">How it works</h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {data.howItWorks.map((step, idx) => (
                <div key={idx} className="bg-neutral-900 rounded-2xl p-6 text-center">
                  <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-cyan-600 font-bold">
                    {idx + 1}
                  </div>
                  <h3 className="font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-white/70">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Content sections */}
      <section className="px-[15px] py-10">
        <div className="mx-auto max-w-[800px] space-y-10">
          {data.sections &&
            data.sections.map((sec, idx) => (
              <div key={idx}>
                <h2 className="text-2xl font-bold mb-4">{sec.heading}</h2>
                {sec.body && <p className="text-white/80">{sec.body}</p>}
                {sec.list && (
                  <ul className="list-disc list-inside space-y-2 text-white/80">
                    {sec.list.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
        </div>
      </section>

      {/* CTA + policy links */}
      <section className="px-[15px] py-12">
        <div className="mx-auto max-w-[800px] text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to get started?</h2>
          <Link
            to={signupHref}
            className="inline-block bg-cyan-600 hover:bg-cyan-700 transition-colors text-white font-semibold rounded-full px-8 py-3"
          >
            Create my headshots
          </Link>
          <p className="text-sm text-white/60 mt-6">
            <Link to="/legal" className="underline">Terms &amp; Privacy</Link>{' · '}
            <Link to="/refund" className="underline">Refund Policy</Link>{' · '}
            <Link to="/delivery" className="underline">Delivery Policy</Link>{' · '}
            <Link to="/contact" className="underline">Contact</Link>
          </p>
        </div>
      </section>
    </main>
  );
};

export default SeoHeadshotPage;
